import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  DateField,
  EmptyState,
  ErrorState,
  Field,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  StatusBadge,
} from "@evolua/ui";
import { useState } from "react";
import {
  useCompleteHrOffboarding,
  useCompleteHrOffboardingItem,
  useCreateHrOffboarding,
  useHrOffboarding,
  useHrOffboardings,
  useReturnHrOffboardingAsset,
  useRevokeHrOffboardingAccess,
  useSaveHrOffboardingInterview,
} from "../api/use-hr.js";

const COMPANY_ID = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";
const ANA_ID = "11111111-1111-4111-8111-111111111111";

/** Desligamento digital — RH (FASE-06-T54). */
export function DesligamentosRhPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastDay, setLastDay] = useState("2026-08-30");
  const [actionError, setActionError] = useState<string | null>(null);
  const [interviewAnswer, setInterviewAnswer] = useState("");
  const [revokeOpen, setRevokeOpen] = useState(false);

  const list = useHrOffboardings();
  const detail = useHrOffboarding(selectedId);
  const create = useCreateHrOffboarding();
  const completeItem = useCompleteHrOffboardingItem();
  const returnAsset = useReturnHrOffboardingAsset();
  const saveInterview = useSaveHrOffboardingInterview();
  const revoke = useRevokeHrOffboardingAccess();
  const complete = useCompleteHrOffboarding();

  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  async function onCreate() {
    setActionError(null);
    try {
      const created = await create.mutateAsync({
        companyId: COMPANY_ID,
        employeeId: ANA_ID,
        lastDay,
        reason: "VOLUNTARY",
      });
      setSelectedId(created.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha ao criar");
    }
  }

  async function onCompleteItem(key: string) {
    if (!selectedId) return;
    setActionError(null);
    try {
      await completeItem.mutateAsync({ id: selectedId, key, note: "ok" });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no checklist");
    }
  }

  async function onReturnAsset(assetId: string) {
    if (!selectedId) return;
    setActionError(null);
    try {
      await returnAsset.mutateAsync({
        id: selectedId,
        assetId,
        status: "RETURNED",
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha no ativo");
    }
  }

  async function onInterview() {
    if (!selectedId) return;
    setActionError(null);
    try {
      await saveInterview.mutateAsync({
        id: selectedId,
        answers: [
          {
            key: "reason",
            question: "Motivo principal da saída?",
            answer: interviewAnswer.trim() || "Não informado",
          },
        ],
      });
      setInterviewAnswer("");
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha na entrevista");
    }
  }

  async function onRevoke() {
    if (!selectedId) return;
    setActionError(null);
    try {
      await revoke.mutateAsync(selectedId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Revogação bloqueada");
    }
  }

  async function onComplete() {
    if (!selectedId) return;
    setActionError(null);
    try {
      await complete.mutateAsync(selectedId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Arquivo bloqueado");
    }
  }

  if (list.isPending) return <Skeleton height="8rem" label="Desligamentos" />;
  if (list.isError || !list.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void list.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Desligamentos (RH)"
        lead="Checklist com responsáveis, devolução de ativos, entrevista, revogação de acesso e arquivamento com retenção (sem hard-delete)."
      />

      <form
        className="evolua-stack evolua-stack--3"
        onSubmit={(e) => {
          e.preventDefault();
          void onCreate();
        }}
      >
        <DateField
          label="Último dia"
          value={lastDay}
          onChange={(e) => setLastDay(e.target.value)}
          aria-label="Último dia"
        />
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Abrindo…" : "Abrir desligamento (Ana)"}
        </Button>
      </form>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {list.data.data.length === 0 ? (
        <EmptyState title="Nenhum desligamento" description="Abra o primeiro acima." />
      ) : (
        <Table
          caption="Desligamentos"
          getRowId={(item) => item.id}
          columns={[
            { id: "name", header: "Colaborador", cell: (item) => item.employeeName },
            {
              id: "status",
              header: "Status",
              cell: (item) => <StatusBadge status={item.status} />,
            },
            {
              id: "progress",
              header: "Progresso",
              align: "numeric",
              cell: (item) => `${item.progressPercent}%`,
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) => (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedId(item.id)}
                >
                  Abrir
                </Button>
              ),
            },
          ]}
          rows={list.data.data}
        />
      )}

      {selectedId && detail.data ? (
        <section className="evolua-stack evolua-stack--3">
          <h2 className="evolua-section-title">
            {detail.data.employeeName} — último dia {detail.data.lastDay}
          </h2>
          <p className="evolua-page-lead">
            Retenção: {detail.data.retention.policyRef} · dados{" "}
            {detail.data.retention.dataDisposition.toLowerCase()} (hard-delete proibido).
          </p>

          <h3 className="evolua-section-title">Checklist</h3>
          <Table
            caption="Checklist"
            getRowId={(item) => item.key}
            columns={[
              { id: "label", header: "Item", cell: (item) => item.label },
              {
                id: "owner",
                header: "Responsável",
                cell: (item) => <Badge tone="neutral">{item.ownerRole}</Badge>,
              },
              {
                id: "status",
                header: "Status",
                cell: (item) => <StatusBadge status={item.status} />,
              },
              {
                id: "actions",
                header: "Ações",
                cell: (item) =>
                  item.status === "PENDING" ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void onCompleteItem(item.key)}
                    >
                      Concluir
                    </Button>
                  ) : (
                    "—"
                  ),
              },
            ]}
            rows={detail.data.checklist}
          />

          <h3 className="evolua-section-title">Ativos</h3>
          <Table
            caption="Ativos"
            getRowId={(asset) => asset.id}
            columns={[
              { id: "label", header: "Ativo", cell: (asset) => asset.label },
              {
                id: "status",
                header: "Status",
                cell: (asset) => <StatusBadge status={asset.status} />,
              },
              {
                id: "actions",
                header: "Ações",
                cell: (asset) =>
                  asset.status === "PENDING" ? (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void onReturnAsset(asset.id)}
                    >
                      Devolver
                    </Button>
                  ) : (
                    "—"
                  ),
              },
            ]}
            rows={detail.data.assets}
          />

          <h3 className="evolua-section-title">Entrevista</h3>
          {detail.data.interview ? (
            <p>
              Registrada em{" "}
              {new Date(detail.data.interview.conductedAt!).toLocaleString("pt-BR")}
            </p>
          ) : (
            <form
              className="evolua-stack evolua-stack--3"
              onSubmit={(e) => {
                e.preventDefault();
                void onInterview();
              }}
            >
              <Field
                label="Motivo da saída"
                type="text"
                placeholder="Motivo da saída"
                value={interviewAnswer}
                onChange={(e) => setInterviewAnswer(e.target.value)}
                aria-label="Motivo da saída"
              />
              <Button type="submit" size="sm">
                Salvar entrevista
              </Button>
            </form>
          )}

          <div className="evolua-stack evolua-stack--3">
            <Button
              type="button"
              disabled={
                revoke.isPending ||
                detail.data.status === "ACCESS_REVOKED" ||
                detail.data.status === "COMPLETED"
              }
              onClick={() => setRevokeOpen(true)}
            >
              Revogar acesso
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={complete.isPending || detail.data.status === "COMPLETED"}
              onClick={() => void onComplete()}
            >
              Arquivar (reter dados)
            </Button>
          </div>

          {detail.data.access.revokedAt ? (
            <p className="evolua-muted">
              Acesso revogado · usuário{" "}
              <Badge tone="warning">{detail.data.access.userStatusAfter}</Badge>
              {detail.data.access.employeeMarkedTerminated
                ? " · colaborador TERMINATED"
                : null}
            </p>
          ) : null}
        </section>
      ) : null}

      <Modal
        open={revokeOpen}
        title="Revogar acesso"
        onClose={() => setRevokeOpen(false)}
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setRevokeOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              disabled={revoke.isPending}
              onClick={() => {
                setRevokeOpen(false);
                void onRevoke();
              }}
            >
              Revogar acesso
            </Button>
          </>
        }
      >
        <p>O acesso à plataforma será revogado neste desligamento. Confirma?</p>
      </Modal>
    </>
  );
}
